import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Replace, Sparkles, XCircle } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import { Lightbox } from '../../../components/ui/Lightbox'
import { Modal } from '../../../components/ui/Modal'
import { SegmentedControl } from '../../../components/ui/SegmentedControl'
import { Select } from '../../../components/ui/Select'
import { AuthorizedMediaImage } from '../../../shared/media/authorizedMedia'
import { useAuthStore } from '../../../stores/authStore'
import { useBackgroundJobsStore } from '../../../stores/backgroundJobsStore'
import { showJobStartedToast, useToastStore } from '../../../stores/toastStore'
import type { ReplaceDesignSourceMode, ReplaceDesignSourceStart } from '../api'
import {
  designAiEditAPI,
  type DesignAiEdit,
  type DesignAiEditMergeMode,
  type DesignAiEditModel,
  type DesignAiEditVerdict,
} from '../aiEditApi'
import { SafeImg } from './design-card/parts'
import { ReplaceScenarioFields, hasPublishedCardStatus } from './design-card/ReplaceScenarioFields'

const POLL_INTERVAL_MS = 3_000
const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000
const PROMPT_MAX = 2000
const HISTORY_LIMIT = 5

const PROMPT_CHIPS = [
  { label: 'Заменить год на 2027', text: 'Заменить год на 2027' },
  { label: 'Исправить ошибку в надписи…', text: 'Исправить ошибку в надписи: ' },
  { label: 'Убрать надпись…', text: 'Убрать надпись ' },
]

const MERGE_MODES: Array<{ value: DesignAiEditMergeMode; title: string; description: string }> = [
  {
    value: 'changes',
    title: 'Только изменённые места (рекомендуется)',
    description: 'Исходник остаётся в полном качестве, от ИИ берутся только участки, которые он поменял',
  },
  {
    value: 'full',
    title: 'Весь лист от ИИ',
    description: 'Для правок по всему листу. Качество может быть ниже исходника',
  },
]

const APPLY_CONFIRM_TEXT =
  'Подтверждаю замену исходника макета результатом ИИ и запуск выбранного сценария. Старый PDF будет перезаписан.'

const formatUsd = (value: number) =>
  `${Number(value || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`

const formatPercent = (value: number) =>
  `${Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`

const isPending = (edit: DesignAiEdit | null) => edit?.status === 'queued' || edit?.status === 'running'

const errorText = (caught: any, fallback: string): string =>
  caught?.response?.data?.error?.message || caught?.message || fallback

/** Вердикт проверки для готового результата; нет данных проверки — считаем предупреждением. */
const verdictOf = (edit: DesignAiEdit | null): DesignAiEditVerdict | null =>
  edit?.status === 'succeeded' ? edit.check?.verdict ?? 'warn' : null

/** Какую правку показать при открытии: незавершённую или свежий неприменённый результат. */
function pickResumable(edits: DesignAiEdit[]): DesignAiEdit | null {
  const now = Date.now()
  return (
    edits.find((edit) => {
      if (isPending(edit)) return true
      if (edit.status !== 'succeeded') return false
      const created = new Date(edit.createdAt).getTime()
      return Number.isFinite(created) && now - created < RESUME_WINDOW_MS
    }) ?? null
  )
}

const VERDICT_STYLE: Record<DesignAiEditVerdict, { box: string; text: string; title: string }> = {
  ok: {
    box: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30',
    text: 'text-emerald-800 dark:text-emerald-200',
    title: 'Геометрия совпадает с исходником',
  },
  warn: {
    box: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
    text: 'text-amber-900 dark:text-amber-200',
    title: 'Есть отличия — проверьте глазами',
  },
  fail: {
    box: 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30',
    text: 'text-red-800 dark:text-red-200',
    title: 'Геометрия нарушена — заменить нельзя',
  },
}

function VerdictIcon({ status, className = 'h-4 w-4 shrink-0' }: { status: DesignAiEditVerdict; className?: string }) {
  if (status === 'ok') return <CheckCircle2 className={`${className} text-emerald-600 dark:text-emerald-400`} aria-hidden="true" />
  if (status === 'warn') return <AlertTriangle className={`${className} text-amber-600 dark:text-amber-400`} aria-hidden="true" />
  return <XCircle className={`${className} text-red-600 dark:text-red-400`} aria-hidden="true" />
}

function PreviewCard({ title, children, onClick }: { title: ReactNode; children: ReactNode; onClick?: () => void }) {
  const body = (
    <div className="flex aspect-[210/297] max-h-80 w-full items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-950">
      {children}
    </div>
  )
  return (
    <div className="min-w-0">
      <div className="mb-2 flex min-h-7 items-center text-sm font-semibold text-gray-700 dark:text-zinc-200">{title}</div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-label="Открыть крупно"
          className="block w-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          {body}
        </button>
      ) : body}
    </div>
  )
}

interface AiEditDesignModalProps {
  isOpen: boolean
  designCode: number | string
  designType?: string | null
  currentPreviewUrl?: string | null
  cardStatuses?: string[]
  onClose: () => void
  onStarted: (result: ReplaceDesignSourceStart) => void
}

export function AiEditDesignModal({
  isOpen,
  designCode,
  designType,
  currentPreviewUrl,
  cardStatuses = [],
  onClose,
  onStarted,
}: AiEditDesignModalProps) {
  const user = useAuthStore((state) => state.user)
  const addJob = useBackgroundJobsStore((state) => state.addJob)
  const showToast = useToastStore((state) => state.showToast)

  const [step, setStep] = useState<1 | 2>(1)
  const [prompt, setPrompt] = useState('')
  const [mergeMode, setMergeMode] = useState<DesignAiEditMergeMode>('changes')
  const [models, setModels] = useState<DesignAiEditModel[]>([])
  const [modelId, setModelId] = useState('')
  const [edits, setEdits] = useState<DesignAiEdit[]>([])
  const [current, setCurrent] = useState<DesignAiEdit | null>(null)
  const [view, setView] = useState<'result' | 'overlay'>('result')
  const [lightbox, setLightbox] = useState<number | null>(null)
  const [sending, setSending] = useState(false)
  const [acceptWarnings, setAcceptWarnings] = useState(false)
  const [mode, setMode] = useState<ReplaceDesignSourceMode>('full_pipeline')
  const [resendPhotos, setResendPhotos] = useState(true)
  const [confirmed, setConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const currentIdRef = useRef<string | null>(null)
  currentIdRef.current = current?.id ?? null

  const hasPublishedCards = hasPublishedCardStatus(cardStatuses)
  const verdict = verdictOf(current)
  const selectedModel = models.find((item) => item.id === modelId) ?? null

  const upsertEdit = useCallback((edit: DesignAiEdit) => {
    setEdits((prev) => {
      const index = prev.findIndex((item) => item.id === edit.id)
      if (index === -1) return [edit, ...prev]
      const next = prev.slice()
      next[index] = edit
      return next
    })
  }, [])

  // Сброс при закрытии (как в EditDesignSourceModal).
  useEffect(() => {
    if (isOpen) return
    setStep(1)
    setPrompt('')
    setMergeMode('changes')
    setEdits([])
    setCurrent(null)
    setView('result')
    setLightbox(null)
    setSending(false)
    setAcceptWarnings(false)
    setMode('full_pipeline')
    setResendPhotos(true)
    setConfirmed(false)
    setSubmitting(false)
    setError('')
  }, [isOpen])

  // Модели и история правок этого макета.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    designAiEditAPI.listModels().then((list) => {
      if (cancelled || !Array.isArray(list)) return
      setModels(list)
      setModelId((prev) => prev || (list.find((item) => item.isDefault) ?? list[0])?.id || '')
    }).catch(() => undefined)
    designAiEditAPI.listForDesign(designCode).then((list) => {
      if (cancelled || !Array.isArray(list)) return
      const sorted = list.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setEdits(sorted)
      setCurrent((prev) => prev ?? pickResumable(sorted))
    }).catch(() => undefined)
    return () => { cancelled = true }
  }, [isOpen, designCode])

  // Опрос незавершённой правки: только при видимой вкладке, интервал чистится при закрытии/смене.
  const pollId = isPending(current) ? current!.id : null
  useEffect(() => {
    if (!isOpen || !pollId) return
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      void designAiEditAPI.get(pollId).then((fresh) => {
        if (!fresh) return
        upsertEdit(fresh)
        if (currentIdRef.current === fresh.id) setCurrent(fresh)
      }).catch(() => undefined)
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [isOpen, pollId, upsertEdit])

  // Другая правка/результат — подтверждения и вид сбрасываются.
  useEffect(() => {
    setAcceptWarnings(false)
    setConfirmed(false)
    setView('result')
  }, [current?.id])

  const send = async (fromResult: boolean) => {
    const text = prompt.trim()
    if (!text || sending) return
    setSending(true)
    setError('')
    try {
      const started = await designAiEditAPI.start(
        {
          designCode,
          prompt: text,
          mergeMode,
          ...(fromResult && current?.status === 'succeeded' ? { baseEditId: current.id } : {}),
        },
        crypto.randomUUID(),
      )
      upsertEdit(started)
      setCurrent(started)
    } catch (caught) {
      setError(errorText(caught, 'Не удалось отправить правку в ИИ'))
    } finally {
      setSending(false)
    }
  }

  const apply = async () => {
    if (!current || current.status !== 'succeeded' || !confirmed) return
    setSubmitting(true)
    setError('')
    try {
      const result = await designAiEditAPI.apply(
        current.id,
        { mode, resend_photos: resendPhotos, accept_warnings: acceptWarnings },
        crypto.randomUUID(),
      )
      if (user?.user_id) {
        addJob({
          jobId: result.jobId,
          userId: user.user_id,
          type: 'design_source_replacement',
          status: 'processing',
          name: `Замена макета #${designCode} (ИИ)`,
          currentStep: 'Замена PDF',
          progress: { current: 0, total: mode === 'designs_only' ? 2 : 3, percentage: 0 },
          steps: [],
          startedAt: new Date(),
          metadata: { design_code: String(designCode), mode, skipped: result.skipped },
        })
      }
      showJobStartedToast(useToastStore.getState(), result.jobId, `Замена макета #${designCode} (ИИ)`)
      if (result.warnings?.length) {
        showToast(result.warnings.join(' '), 'warning')
      }
      onStarted(result)
      onClose()
    } catch (caught) {
      setError(errorText(caught, 'Не удалось запустить замену макета'))
    } finally {
      setSubmitting(false)
    }
  }

  const canNext = verdict === 'ok' || (verdict === 'warn' && acceptWarnings)
  const nextTitle = verdict === 'fail'
    ? 'Геометрия нарушена — заменить макет этим результатом нельзя'
    : verdict === 'warn' && !acceptWarnings
      ? 'Отметьте, что проверили результат глазами'
      : !verdict
        ? 'Сначала получите результат от ИИ'
        : undefined

  const lightboxItems = useMemo(() => {
    const items: Array<{ src: string; label: string }> = []
    if (current?.files.result) items.push({ src: current.files.result, label: 'Стало' })
    if (current?.files.overlay) items.push({ src: current.files.overlay, label: 'Изменения' })
    return items
  }, [current?.files.result, current?.files.overlay])

  const shownSrc = (view === 'overlay' ? current?.files.overlay : current?.files.result) ?? null
  const hasResult = current?.status === 'succeeded' || current?.status === 'applied'
  const busy = sending || submitting

  const openLightbox = () => {
    const wanted = view === 'overlay' ? 'Изменения' : 'Стало'
    const index = lightboxItems.findIndex((item) => item.label === wanted)
    if (index >= 0) setLightbox(index)
  }

  const resultTitle: ReactNode = hasResult && current?.files.overlay ? (
    <SegmentedControl<'result' | 'overlay'>
      size="sm"
      aria-label="Что показать"
      value={view}
      onChange={setView}
      options={[
        { value: 'result', label: 'Стало' },
        { value: 'overlay', label: 'Изменения' },
      ]}
    />
  ) : 'Стало'

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Изменить с ИИ"
        subtitle={`Макет #${designCode} · шаг ${step} из 2`}
        size="xl"
        layer="nested"
        closeOnOverlayClick={!busy}
        closeOnEsc={!busy}
        footer={
          <>
            <Button variant="secondary" onClick={step === 1 ? onClose : () => setStep(1)} disabled={submitting}>
              {step === 1 ? 'Отмена' : 'Назад'}
            </Button>
            {step === 1 ? (
              <Button variant="primary" onClick={() => setStep(2)} disabled={!canNext} title={nextTitle}>
                Далее
              </Button>
            ) : (
              <Button
                variant="primary"
                icon={<Replace className="h-4 w-4" />}
                loading={submitting}
                disabled={!confirmed}
                onClick={() => { void apply() }}
              >
                Заменить макет
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-5">
          {step === 1 ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <PreviewCard title="Было">
                  {currentPreviewUrl ? (
                    <SafeImg src={currentPreviewUrl} alt={`Текущий макет ${designCode}`} className="h-full w-full object-contain" />
                  ) : (
                    <span className="text-xs text-gray-400">Текущее превью отсутствует</span>
                  )}
                </PreviewCard>
                <PreviewCard title={resultTitle} onClick={hasResult && shownSrc ? openLightbox : undefined}>
                  {isPending(current) ? (
                    <span className="animate-pulse px-3 text-center text-xs text-gray-500 dark:text-zinc-400">
                      ИИ рисует… обычно 1–2 минуты
                    </span>
                  ) : hasResult && shownSrc ? (
                    <AuthorizedMediaImage
                      key={shownSrc}
                      src={shownSrc}
                      alt={view === 'overlay' ? `Изменения макета ${designCode}` : `Результат ИИ для макета ${designCode}`}
                      lazy={false}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="px-3 text-center text-xs text-gray-400">Здесь появится результат</span>
                  )}
                </PreviewCard>
              </div>

              {current?.status === 'failed' && (
                <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
                  {current.error?.message || 'ИИ не смог выполнить правку'}
                </div>
              )}

              {verdict && current?.check && (
                <div className={`rounded-lg border p-3 ${VERDICT_STYLE[verdict].box}`}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                    Проверка оформления
                  </p>
                  <div className={`flex items-center gap-2 text-sm font-semibold ${VERDICT_STYLE[verdict].text}`}>
                    <VerdictIcon status={verdict} className="h-5 w-5 shrink-0" />
                    <span>{VERDICT_STYLE[verdict].title}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">
                    Изменено {formatPercent(current.check.changedPercent)} листа
                  </p>
                  {current.check.items.length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {current.check.items.map((item) => (
                        <li key={item.key} className="flex items-start gap-2 text-sm text-gray-800 dark:text-zinc-200">
                          <VerdictIcon status={item.status} className="mt-0.5 h-4 w-4 shrink-0" />
                          <span>
                            <span className="font-medium">{item.label}</span>
                            {item.detail && <span className="text-gray-600 dark:text-zinc-400"> — {item.detail}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="ai-edit-prompt" className="mb-1 block text-sm font-medium text-gray-700 dark:text-zinc-200">
                  Что изменить
                </label>
                <textarea
                  id="ai-edit-prompt"
                  rows={3}
                  maxLength={PROMPT_MAX}
                  value={prompt}
                  disabled={isPending(current)}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder="Например: замени год 2025 на 2027, шрифт и цвет как у остальной надписи"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-2">
                    {PROMPT_CHIPS.map((chip) => (
                      <Chip key={chip.label} disabled={isPending(current)} onClick={() => setPrompt(chip.text)}>
                        {chip.label}
                      </Chip>
                    ))}
                  </div>
                  <span className="text-xs text-gray-400 dark:text-zinc-500">{prompt.length} / {PROMPT_MAX}</span>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-700 dark:text-zinc-200">Модель</p>
                  <div className="flex items-center gap-3">
                    <Select
                      aria-label="Модель"
                      value={modelId}
                      onValueChange={setModelId}
                      containerClassName="min-w-0 flex-1"
                      disabled={models.length === 0}
                    >
                      {models.map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </Select>
                    {selectedModel && (
                      <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-400">
                        резерв до {formatUsd(selectedModel.reserveUsd)}
                      </span>
                    )}
                  </div>
                  {designType && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
                      Тип макета: <span className="font-mono">{designType}</span>
                    </p>
                  )}
                </div>
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-700 dark:text-zinc-200">Как вклеить</p>
                  <div className="space-y-2" role="radiogroup" aria-label="Как вклеить">
                    {MERGE_MODES.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        role="radio"
                        aria-checked={mergeMode === item.value}
                        onClick={() => setMergeMode(item.value)}
                        className={`block w-full rounded-lg border p-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                          mergeMode === item.value
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
                            : 'border-gray-200 dark:border-zinc-700'
                        }`}
                      >
                        <span className="block text-sm font-semibold">{item.title}</span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-zinc-400">{item.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {current?.status === 'succeeded' ? (
                  <>
                    <Button
                      variant="primary"
                      icon={<Sparkles className="h-4 w-4" />}
                      loading={sending}
                      disabled={!prompt.trim()}
                      onClick={() => { void send(true) }}
                    >
                      Уточнить и повторить
                    </Button>
                    <Button variant="secondary" disabled={sending || !prompt.trim()} onClick={() => { void send(false) }}>
                      Начать с исходника
                    </Button>
                    <span className="text-xs text-gray-500 dark:text-zinc-400">
                      Правка пойдёт от последнего результата
                    </span>
                  </>
                ) : (
                  <Button
                    variant="primary"
                    icon={<Sparkles className="h-4 w-4" />}
                    loading={sending || isPending(current)}
                    disabled={!prompt.trim()}
                    onClick={() => { void send(false) }}
                  >
                    Отправить в ИИ
                  </Button>
                )}
              </div>

              {edits.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                    Прошлые правки
                  </p>
                  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-zinc-800 dark:border-zinc-700">
                    {edits.slice(0, HISTORY_LIMIT).map((edit) => {
                      const rowVerdict = edit.status === 'succeeded' || edit.status === 'applied' ? edit.check?.verdict : null
                      return (
                        <li key={edit.id}>
                          <button
                            type="button"
                            onClick={() => setCurrent(edit)}
                            aria-current={current?.id === edit.id}
                            className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:hover:bg-zinc-800/60 ${
                              current?.id === edit.id ? 'bg-indigo-50/60 dark:bg-indigo-950/20' : ''
                            }`}
                          >
                            <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-400">
                              {new Date(edit.createdAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-zinc-200">{edit.prompt}</span>
                            {rowVerdict && <VerdictIcon status={rowVerdict} />}
                            <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-400">
                              {edit.status === 'applied' ? 'применён'
                                : edit.status === 'queued' ? 'в очереди'
                                : edit.status === 'running' ? 'ИИ рисует'
                                : edit.status === 'failed' ? 'ошибка'
                                : 'готов'}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {verdict === 'warn' && (
                <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-amber-300 p-3 text-sm dark:border-amber-800">
                  <input
                    type="checkbox"
                    checked={acceptWarnings}
                    onChange={(event) => setAcceptWarnings(event.target.checked)}
                    className="mt-0.5"
                  />
                  <span>Я проверил результат глазами, применить несмотря на предупреждения</span>
                </label>
              )}
            </>
          ) : (
            <>
              <div className="flex items-start gap-4">
                <div className="h-32 w-24 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white p-1 dark:border-zinc-700 dark:bg-zinc-950">
                  {current?.files.result && (
                    <AuthorizedMediaImage
                      src={current.files.result}
                      alt={`Результат ИИ для макета ${designCode}`}
                      lazy={false}
                      className="h-full w-full object-contain"
                    />
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-zinc-300">
                  Правка меняет картинку макета, поэтому товары и карточки должны обновиться вместе с ним.
                  Выберите, что пересоздать после замены.
                </p>
              </div>
              <ReplaceScenarioFields
                mode={mode}
                onModeChange={(value) => { setMode(value); setConfirmed(false) }}
                resendPhotos={resendPhotos}
                onResendPhotosChange={setResendPhotos}
                confirmed={confirmed}
                onConfirmedChange={setConfirmed}
                hasPublishedCards={hasPublishedCards}
                confirmText={APPLY_CONFIRM_TEXT}
              />
            </>
          )}

          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          )}
        </div>
      </Modal>
      <Lightbox
        isOpen={lightbox !== null}
        items={lightboxItems}
        index={lightbox ?? 0}
        onIndexChange={setLightbox}
        onClose={() => setLightbox(null)}
      />
    </>
  )
}

export default AiEditDesignModal
